import { useEffect, useRef } from 'react';
import { useLoaderData } from 'react-router';
import { useCountryContext } from '@archipro-website/localisation';
import { RoccoLink } from '@rocco/components/link';
import type { loader as homeLoader } from '~/routes/_app._index';
import { SectionLabel } from '../components/SectionLabel';
import { CategoryPills } from '../components/CategoryPills';
import { ComingSoonPill } from '../components/ComingSoonPill';
import { ScrollReveal } from '../components/ScrollReveal';
import heroImg from '../images/products/hero.jpg';
import vaseImg from '../images/products/parallax/vase.png';
import diningTableImg from '../images/products/parallax/dining-table.png';
import plantersImg from '../images/products/parallax/planters.png';
import cushionImg from '../images/products/parallax/cushion.png';
import coffeeTableImg from '../images/products/parallax/coffee-table.png';
import drawersImg from '../images/products/parallax/drawers.png';
import chairImg from '../images/products/parallax/chair.png';

/**
 * The homepage's previous ProductsSection, drawn while `revamp_2026` is off. Kept as it
 * was so the flag-off homepage does not change; delete it with the flag.
 */

/**
 * Browse Products + Materials (designer parallax demo, white).
 *
 * An editorial header (eyebrow, giant display heading, categories, description,
 * Coming Soon pill + View all CTA) above an aspect-locked parallax montage: a
 * giant "Products" wordmark that drifts slowly down in the background while
 * eight floating product cut-outs rush up in the foreground. Static (no live
 * product data yet). The 1920 × 2843 frame is percentage-based so the montage
 * scales fluidly from desktop down to mobile.
 */

/** Per-tile placement within the 1920 × 2843 frame (percentages, from the demo). */
interface ParallaxTile {
    image: string;
    /** Left/top as a percentage of the frame's width/height. */
    left: number;
    top: number;
    /** Parallax depth — foreground tiles (higher) out-run the background. */
    depth: number;
}

/** All tiles share the demo's square box width (% of frame width). */
const TILE_WIDTH = 23.2812;

const TILES: ParallaxTile[] = [
    { image: vaseImg, left: 73.9, top: 5.65, depth: 0.9 },
    { image: diningTableImg, left: 26.76, top: 11.06, depth: 1.15 },
    { image: plantersImg, left: 57.75, top: 29.23, depth: 0.75 },
    { image: cushionImg, left: 2.71, top: 38.7, depth: 1.25 },
    { image: coffeeTableImg, left: 34.42, top: 49.97, depth: 0.85 },
    { image: diningTableImg, left: 74.16, top: 56.43, depth: 1.2 },
    { image: drawersImg, left: 2.71, top: 69.81, depth: 1.0 },
    { image: chairImg, left: 42.7, top: 75.48, depth: 1.3 },
];

/**
 * Demo parallax: one rAF-throttled scroll handler writes transforms straight
 * onto the layers — no layout thrash. The wordmark lags the page (slow
 * background, +0.18); the tiles out-run it (fast foreground, −0.08 × depth).
 * Reduced motion → no transforms, everything sits at its laid-out position.
 */
const useParallax = (frameRef: React.RefObject<HTMLElement | null>) => {
    useEffect(() => {
        const frame = frameRef.current;
        if (!frame) return;
        const reduce = window.matchMedia(
            '(prefers-reduced-motion: reduce)',
        ).matches;
        if (reduce) return;

        const title = frame.querySelector<HTMLElement>('[data-parallax-title]');
        const tiles = Array.from(
            frame.querySelectorAll<HTMLElement>('[data-depth]'),
        );
        let raf = 0;

        const update = () => {
            raf = 0;
            const rect = frame.getBoundingClientRect();
            const scrolled = window.innerHeight - rect.top;
            if (title) {
                title.style.transform = `translate3d(0, ${(
                    scrolled * 0.18
                ).toFixed(1)}px, 0)`;
            }
            for (const tile of tiles) {
                const depth = Number(tile.dataset.depth) || 1;
                tile.style.transform = `translate3d(0, ${(
                    scrolled *
                    -0.08 *
                    depth
                ).toFixed(1)}px, 0)`;
            }
        };

        const onScroll = () => {
            if (!raf) raf = requestAnimationFrame(update);
        };

        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [frameRef]);
};

/** Filled rounded CTA in the header (markets where Products has shipped). */
const ViewAllProductsCTA = () => (
    <RoccoLink
        to="/products"
        className="inline-flex w-fit items-center justify-center rounded-[var(--radius-btn-l)] bg-[#181818] font-sans font-normal tracking-[-0.03em] whitespace-nowrap text-white shadow-[0px_3.243px_11.351px_rgba(0,0,0,0.11)] max-md:rounded-[var(--radius-btn-m)] max-md:px-7 max-md:py-3 max-md:text-base md:h-[3.125vw] md:min-w-[11.99vw] md:px-[1.436vw] md:text-[1.182vw]"
    >
        View all Products
    </RoccoLink>
);

export const ProductsSectionLegacy = () => {
    const { productPopularCategories } = useLoaderData<typeof homeLoader>();
    const frameRef = useRef<HTMLDivElement>(null);
    useParallax(frameRef);

    // US is pre-launch ("Coming Soon - Fall 2026"); AU/NZ link to the live directory.
    const isComingSoon = useCountryContext().byCountry({
        US: true,
        AU: false,
        NZ: false,
    });

    return (
        <section className="w-full overflow-x-clip bg-white text-[#1f1f21]">
            {/* Header */}
            <ScrollReveal className="flex flex-col max-md:gap-12 max-md:px-6 max-md:pt-10 md:gap-[13.54vw] md:px-[1.302vw] md:pt-[8.33vw]">
                <div className="flex flex-col max-md:gap-6 md:gap-[4.79vw]">
                    <div className="flex w-full flex-col gap-[0.885vw]">
                        <div className="h-px w-full bg-black/20" />
                        <SectionLabel className="tracking-[-0.01em] max-md:text-[1.75vw] md:text-[1.667vw]">
                            (03 – Products)
                        </SectionLabel>
                    </div>
                    <h2 className="m-0 font-sans font-normal max-md:text-[11.72vw] max-md:leading-[11.98vw] max-md:tracking-[-0.49vw] md:w-[88.6vw] md:text-[10.417vw] md:leading-[9.375vw] md:tracking-[-0.4375vw]">
                        Browse Products + Materials
                    </h2>
                </div>

                <div className="flex max-md:gap-5 md:gap-[24vw]">
                    <CategoryPills
                        label="Popular categories:"
                        items={productPopularCategories}
                        className="max-md:w-[38%] md:w-[24.3vw]"
                    />
                    <div className="flex flex-col max-md:flex-1 max-md:gap-5 md:w-[43.96vw] md:gap-[3.49vw]">
                        <p className="m-0 font-sans leading-[1.3] font-normal max-md:text-[3.13vw] md:text-[2.03vw]">
                            Explore products and materials architects love to
                            use. From cladding and lighting to finishes and
                            more. Our marketplace connects you with top brands
                            ensuring access to the best materials to complete
                            your space.
                        </p>
                        <div className="flex items-center max-md:gap-4 md:gap-[0.5vw]">
                            {isComingSoon ? (
                                <ComingSoonPill />
                            ) : (
                                <ViewAllProductsCTA />
                            )}
                        </div>
                    </div>
                </div>
            </ScrollReveal>

            {/* Feature banner — wide hero image between the header and the montage */}
            <div className="max-md:mt-10 max-md:px-6 md:mt-[8.33vw] md:px-[1.302vw]">
                <img
                    src={heroImg}
                    alt=""
                    aria-hidden="true"
                    className="aspect-[1761/825] w-full rounded-tile object-cover"
                    loading="lazy"
                />
            </div>

            {/*
              Parallax montage — aspect-locked to the 1920 × 2843 demo frame.
              The negative bottom margin pulls the next section up into the dead
              space the parallax opens beneath the tiles. `margin-bottom` %
              resolves against the parent's width, so -25% == 25% of frame width
              at any viewport. This is safe (no tile clip) because the foreground
              tiles drift upward as you scroll, so each tile's bottom has already
              risen clear of the reclaimed band by the time it scrolls into view
              — the lowest tile (chair) clears it by ~0.75% of frame width at its
              tightest point. Tile positions and the parallax motion are untouched.

              Applied on mobile too: the lowest tile's static bottom already
              leaves ~13% of frame width as dead space, and the tiles drift
              further up by the scroll point where the next section rises into
              view, so the reclaim stays clear of them.
            */}
            <div
                ref={frameRef}
                className="relative -mb-[25%] w-full"
                style={{ aspectRatio: '1920 / 2843' }}
            >
                {/* Giant wordmark — slow background, drifts down on scroll */}
                <p
                    data-parallax-title
                    className="absolute left-0 z-0 m-0 w-full text-center font-sans font-normal will-change-transform"
                    style={{
                        top: '25%',
                        fontSize: 'clamp(80px, 10.4vw, 200px)',
                        lineHeight: 1,
                        letterSpacing: '-0.04em',
                    }}
                >
                    Products
                </p>

                {/* Floating product tiles — fast foreground, rush up on scroll */}
                {TILES.map((tile, index) => (
                    <div
                        key={`${tile.image}-${index}`}
                        data-depth={tile.depth}
                        className="absolute z-10 will-change-transform"
                        style={{
                            left: `${tile.left}%`,
                            top: `${tile.top}%`,
                            width: `${TILE_WIDTH}%`,
                            aspectRatio: '1 / 1',
                        }}
                    >
                        <img
                            src={tile.image}
                            alt=""
                            aria-hidden="true"
                            className="block size-full object-contain"
                            loading="lazy"
                        />
                    </div>
                ))}
            </div>
        </section>
    );
};
